import React, { useRef } from 'react';
import { App, Form, Input, Space, theme, Typography } from 'antd';
import { useGetScreenSize } from '../../common/hooks/useGetScreenSize';
import { useAppStore } from '../../common/store';
import { sendDocumentTemplatePendoTrackingEvent } from '../../common/tracking/documentGeneratorEvent';
import {
	DocumentTemplateCreateTrackEvent,
	DocumentTemplateEditTrackEvent,
} from '../../common/tracking/events';
import { MAX_DESCRIPTION_LENGTH } from '../../constants';
import { DocgenClient } from '../../data/docgenClient';
import { handleError, handleErrorWithServerMessage } from '../../data/errorHandler';
import { PatchPayloadItem } from '../../data/interfaces/APIRequest';
import { reviewPathSlug } from '../../routes';
import { t } from '../../utils';
import { navigationEventManager } from '../../utils/navigationEventManager';
import { SecurityOptions } from './SecurityOptions';

const { TextArea } = Input;
const { Title, Text } = Typography;

const useReviewPageNavigation = (reviewSettings, onDrawerClose, app) => {
	const { notification } = App.useApp();
	const { getDocumentTemplate, updateTemplateMetadata, resetStore } = useAppStore(
		state => ({
			getDocumentTemplate: state.getDocumentTemplate,
			updateTemplateMetadata: state.updateTemplateMetadata,
			resetStore: state.resetStore,
		})
	);

	const navigationEventObject = {
		async onNext() {
			const { documentTemplate, updateDocumentTemplate, setIsLoading, templateMetadata } =
				useAppStore.getState();
			if (!documentTemplate.name) {
				handleError(t('reviewPage.templateNameBlank'));
			} else {
				setIsLoading(true);
				try {
					const reviewPageSettings = [
						'name',
						'description',
						'isKbaEnabled',
						'isPasscodeEnabled',
						'status',
					];

					const eventName =
						documentTemplate.status === 'Live'
							? DocumentTemplateEditTrackEvent
							: DocumentTemplateCreateTrackEvent;

					let isCreateFlow = false;
					if (documentTemplate.status !== 'Live') {
						isCreateFlow = true;
					}

					documentTemplate.status = 'Live';

					const payload: PatchPayloadItem[] = [];
					reviewPageSettings.forEach(setting => {
						if (documentTemplate[setting] !== reviewSettings[setting]) {
							payload.push({
								op: 'replace',
								path: `/${setting}`,
								value: documentTemplate[setting],
							});
						}
					});

					if (payload.length) {
						const patchResponse = await DocgenClient.updateDocumentTemplate(
							payload,
							documentTemplate.rid
						);
						updateDocumentTemplate(patchResponse);
						notification.success({
							message: t('createdTemplateSuccess'),
						});

						//fetch updated template
						await getDocumentTemplate(documentTemplate.rid);
					}

					// We are updating the store with the updated document template, so using that itself for pendo event
					// moved it out of if block because we may have updated the template in previous pages
					// so anyways sending the event so that we can track the user intent to complete edit flow
					sendDocumentTemplatePendoTrackingEvent(eventName, documentTemplate);

					if (
						(!templateMetadata?.source || templateMetadata?.source !== 'esign') &&
						isCreateFlow
					) {
						setTimeout(() => {
							app.showDrawer('urn:sfmodal:docgen:modals:document-template-details', {
								drawerProps: {
									title: documentTemplate.name,
								},
								onCancelInteraction: () => {
									resetStore();
								},
								onCompleteInteraction: () => {
									resetStore();
								},
							});
						}, 500);
					}
					onDrawerClose();
				} catch {
					handleErrorWithServerMessage(t('errorMessages.updateDocumentTemplateError'));
				} finally {
					setIsLoading(false);
				}
			}
		},
		onPrevious() {
			updateTemplateMetadata({ currentStep: 1 });
		},
	};

	navigationEventManager.register(reviewPathSlug, navigationEventObject);
};

export const Review = ({ onDrawerClose, app }) => {
	const { documentTemplate, updateDocumentTemplate } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		updateDocumentTemplate: state.updateDocumentTemplate,
	}));

	const { isExtraSmallScreen } = useGetScreenSize();

	const { token } = theme.useToken();

	const reviewSettingsRef = useRef(documentTemplate);

	useReviewPageNavigation(reviewSettingsRef.current, onDrawerClose, app);

	return (
		<Space
			direction="vertical"
			style={{
				width: isExtraSmallScreen ? '100%' : '50%',
				paddingBlockStart: token.paddingLG,
				paddingInline: isExtraSmallScreen ? token.paddingMD : 0,
				paddingBlockEnd: isExtraSmallScreen ? token.paddingMD : 0,
			}}
		>
			<Title level={4} style={{ marginBottom: token.marginLG }}>
				{t('reviewAndSave')}
			</Title>
			<Form layout="vertical">
				<Form.Item label={<Text strong>{t('name')}</Text>} required>
					<Input
						placeholder={t('reviewPage.templatePlaceholderText')}
						value={documentTemplate?.name}
						onChange={e => updateDocumentTemplate({ name: e.target.value })}
						aria-label={t('reviewPage.templatePlaceholderText')}
					/>
				</Form.Item>
				<Form.Item label={<Text strong>{t('description')}</Text>}>
					<TextArea
						count={{
							show: true,
						}}
						maxLength={MAX_DESCRIPTION_LENGTH}
						onChange={e => updateDocumentTemplate({ description: e.target.value })}
						value={documentTemplate?.description}
						rows={6}
						placeholder={t('reviewPage.descriptionPlaceholderText')}
						aria-label={t('reviewPage.descriptionPlaceholderText')}
					/>
				</Form.Item>
				<SecurityOptions />
			</Form>
		</Space>
	);
};
